import { useState } from "react";
import { RotateCcw } from "lucide-react";
import type { SolvePhase } from "@playbookai/types";
import { ConfirmDialog, Tooltip } from "@playbookai/ui";

/** Mounted with a current-phase key so a remote transition dismisses stale confirmation. */
export function ReturnPhaseControl({
  previousPhase,
  disabled,
  onReturn,
}: {
  readonly previousPhase: SolvePhase;
  readonly disabled: boolean;
  readonly onReturn: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* DS keep: WorkspaceControlButton and any ghost Button in this railControls group paint a white glyph; SN-21 brightens only Back, so Return keeps today's secondary-grey ghost circle. */}
      <Tooltip content={`Return to ${previousPhase}`}>
        {/* eslint-disable-next-line ds/no-hand-built -- DS keep: a Button here takes the rail look (white glyph); no option keeps the secondary ghost */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          aria-label={`Return to ${previousPhase}`}
          className="focus-ring press inline-flex aspect-square h-8 w-8 shrink-0 items-center justify-center gap-1.5 rounded-full border border-transparent bg-transparent p-0 text-xs font-medium text-pb-text-secondary transition-[color,background-color,background-image,border-color,box-shadow,transform] hover:pb-overlay-paint hover:text-pb-text active:pb-overlay-paint-strong disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none"
        >
          <RotateCcw className="h-4 w-4" aria-hidden />
        </button>
      </Tooltip>
      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        title={`Return to ${previousPhase}?`}
        confirmLabel={`Return to ${previousPhase}`}
        tone="neutral"
        confirmDisabled={disabled}
        onConfirm={() => {
          setOpen(false);
          onReturn();
        }}
      >
        This changes the current phase for everyone. All existing work, later
        phases, and phase-lock settings will be preserved.
      </ConfirmDialog>
    </>
  );
}
